// ── Push dispatcher heartbeat ──────────────────────────────────────
// Netlify cron registration is unreliable through our deploy path, so
// instead of relying on a scheduled function we ping the dispatcher
// (/api/push-run) from the client while the app is open. Whenever anyone
// is using BCHNostr, the dispatcher runs and delivers pending push
// notifications (zaps, replies, mentions) to all subscribers.
//
// A localStorage timestamp acts as a lightweight cross-tab throttle so
// many open tabs/users don't all fire at once — at most one run per
// INTERVAL_MS across the whole browser.

const LAST_RUN_KEY = 'bchnostr:push-heartbeat-last';
const INTERVAL_MS = 2 * 60 * 1000; // 2 minutes
// Small jitter so simultaneous visitors don't collide on the exact tick.
const JITTER_MS = 15 * 1000;

let timer: ReturnType<typeof setInterval> | null = null;

function shouldRun(): boolean {
  try {
    const last = Number(localStorage.getItem(LAST_RUN_KEY) || 0);
    return Date.now() - last >= INTERVAL_MS;
  } catch {
    return true;
  }
}

function markRun(): void {
  try {
    localStorage.setItem(LAST_RUN_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

async function ping(): Promise<void> {
  if (!shouldRun()) return;
  // Claim the slot BEFORE the request so concurrent tabs don't double-fire.
  markRun();
  try {
    await fetch('/api/push-run', { method: 'GET', keepalive: true });
  } catch {
    /* fire-and-forget — a failed ping just means the next one retries */
  }
}

/**
 * Start the heartbeat. Safe to call multiple times (idempotent).
 * Fires once shortly after startup, then on a 2-minute cadence, and also
 * when the tab regains focus (so returning users trigger a fresh run).
 */
export function startPushHeartbeat(): void {
  if (timer) return;

  // Initial kick after a short, jittered delay so first paint isn't
  // competing with the request.
  const initialDelay = 5000 + Math.random() * JITTER_MS;
  setTimeout(ping, initialDelay);

  timer = setInterval(ping, INTERVAL_MS + Math.random() * JITTER_MS);

  // Trigger when the tab becomes visible again (cheap; throttled by
  // shouldRun so it won't over-fire).
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') ping();
  });
}
